import {
  createBillingAddress,
  fetchBillingAddress,
  updateBillingAddress,
} from "../../app-main/actions/fee-profile";
import { createSpace, updateSpace } from "../../app-main/actions/space";
import { getSpaceSubmissionErrorMessage } from "../forms/space-submission-error.utils";

import type {
  AddressFormSubmitResult,
  SpaceResponse,
  SubmitAddressFormArgs,
} from "./AddSpaceAddressForm.types";
import {
  buildBillingAddressPayload,
  buildNextStepUrl,
  buildSpaceAddressPayload,
} from "./AddSpaceAddressForm.utils";

/**
 * Billing address first, then the space. Returns a result rather than throwing, so
 * the caller can hand the message to `form.setError("root.serverError", …)`. On
 * success the browser redirects and the return value is never observed.
 */
const submitAddressForm = ({
  values,
  dispatch,
  intl,
  accessToken,
  ssoId,
  spaceId,
  feeProfileId,
  isEdit,
  hasBillingAddress,
  hasListedSpace,
}: SubmitAddressFormArgs): Promise<AddressFormSubmitResult> => {
  const spaceAddressPayload = buildSpaceAddressPayload(values);

  const failWith = (response: unknown): AddressFormSubmitResult => ({
    status: "error",
    message: getSpaceSubmissionErrorMessage(response, intl),
  });

  const goToNextStep = (response: unknown): AddressFormSubmitResult => {
    // NOTE: add analytics here
    window.location.href = buildNextStepUrl({
      isEdit,
      spaceId: (response as SpaceResponse)?.space?._id,
    });

    return { status: "success" };
  };

  const runSpaceCall = () =>
    dispatch(
      isEdit || spaceId
        ? updateSpace(spaceId, accessToken, spaceAddressPayload)
        : createSpace(accessToken, spaceAddressPayload),
    );

  // A host who already listed a space has billing on file and a locked country, so
  // there is nothing to write on the billing side.
  if (hasListedSpace && hasBillingAddress) {
    return runSpaceCall().then(goToNextStep, failWith);
  }

  const billingAddressPayload = {
    ...buildBillingAddressPayload(values),
    type: "BILLING",
    obj_state: "ACTIVE",
  };

  const runBillingCall = () =>
    dispatch(
      hasBillingAddress
        ? updateBillingAddress(accessToken, feeProfileId, {
            ...billingAddressPayload,
            sso_id: ssoId,
          })
        : createBillingAddress(accessToken, ssoId, billingAddressPayload),
    );

  // The billing address exists by the time the space call fails, so re-read it
  // before surfacing the error: a retry has to update that profile rather than
  // create a second one.
  const resyncBillingThenFail = (spaceResponse: unknown) =>
    dispatch(fetchBillingAddress(accessToken, ssoId))
      .catch(() => null)
      .then(() => failWith(spaceResponse));

  // The billing rejection is handled as the second argument to `then` rather than a
  // trailing `catch`, so it cannot swallow the space error raised above it.
  return runBillingCall().then(
    () => runSpaceCall().then(goToNextStep, resyncBillingThenFail),
    failWith,
  );
};

export { submitAddressForm };
